/**
 * BundleToggleWidget — "Bundle product" switch for item create flow.
 *
 * Only visible during item creation (not for existing items — those use
 * the overflow menu actions "Convert to bundle" / "Remove bundle").
 *
 * Sets a virtual `__bundle_mode` field in formData. The useItemSave hook
 * reads this to create a Product Bundle after the Item is created.
 *
 * Plan: 171-bundle-product-ux, Epic 04
 */

import { Card, CardContent } from "@/components/ui/card";
import { CARD } from "@/config/design-tokens";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";

export function BundleToggleWidget({
  formData,
  onFieldChange,
}: ExperienceWidgetProps) {
  const isNew = !formData?.name || String(formData.name).startsWith("new-");

  // Only show on create (existing items use overflow menu for convert/remove)
  if (!isNew) return null;

  // Mutual exclusivity: hide if "Has variants" is enabled (templates can't be bundles)
  if (formData?.has_variants === 1 || formData?.has_variants === true) return null;

  // Hide for bookable items — bundles not relevant for bookings
  if (formData?.is_bookable === 1 || formData?.is_bookable === true) return null;

  const isBundle = formData?.__bundle_mode === 1;

  return (
    <Card className={CARD}>
      <CardContent className="flex items-center justify-between py-3 px-5">
        <Label htmlFor="bundle-toggle" className="text-sm font-medium cursor-pointer">
          Bundle product
        </Label>
        <Switch
          id="bundle-toggle"
          checked={!!isBundle}
          onCheckedChange={(checked) => {
            onFieldChange("__bundle_mode", checked ? 1 : 0);
          }}
        />
      </CardContent>
    </Card>
  );
}
